import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react';
import {
  applyChatAppearance,
  getChatAppearance,
  isChatAppearancePath,
  isChatDarkModeEnabled,
  isChatSimpleViewEnabled,
  setChatAppearance,
  subscribeChatAppearance,
} from '../utils/chatAppearance';
import { trackChatUxEvent } from '../utils/eventTracker';

const ACTIVE_EVENT_KEY = 'wq_chat_appearance_active_sent';

/** /chat 화면 모드 설정 구독 + 바꾸기. 바꿀 때 값만(글 없음) 계측한다. */
export default function useChatAppearance() {
  const appearance = useSyncExternalStore(subscribeChatAppearance, getChatAppearance, getChatAppearance);

  const setTheme = useCallback((theme, entry = 'settings') => {
    const previous = getChatAppearance();
    if (previous.theme === theme) return;
    const next = setChatAppearance({ theme });
    trackChatUxEvent('appearance_theme_change', {
      entry,
      themeSetting: next.theme,
      themeEffective: next.effectiveTheme,
      simpleView: next.simpleView,
    });
  }, []);

  const setSimple = useCallback((simple, entry = 'settings') => {
    const previous = getChatAppearance();
    if (previous.simple === simple) return;
    const next = setChatAppearance({ simple });
    trackChatUxEvent('appearance_simple_change', {
      entry,
      themeSetting: next.theme,
      themeEffective: next.effectiveTheme,
      simpleView: next.simpleView,
    });
  }, []);

  return {
    ...appearance,
    darkModeAvailable: isChatDarkModeEnabled(),
    simpleViewAvailable: isChatSimpleViewEnabled(),
    setTheme,
    setSimple,
  };
}

export const useChatSimpleView = () => useSyncExternalStore(
  subscribeChatAppearance,
  () => getChatAppearance().simpleView,
  () => getChatAppearance().simpleView,
);

/**
 * App 레이아웃에서 한 번 호출: /chat 경로에 있는 동안만 <html> 에 모드 속성을 단다.
 * 기본값이 아닌 모드로 /chat 을 연 세션은 1회 `appearance_active` 로 남긴다(사용률 측정).
 */
export const useChatAppearanceRoot = (pathname) => {
  const appearance = useSyncExternalStore(subscribeChatAppearance, getChatAppearance, getChatAppearance);
  const active = isChatAppearancePath(pathname);
  const { effectiveTheme, simpleView, theme } = appearance;

  useLayoutEffect(() => {
    applyChatAppearance(document.documentElement, { active, effectiveTheme, simpleView });
  }, [active, effectiveTheme, simpleView]);

  useLayoutEffect(() => {
    if (!active) return;
    const themeCustomized = isChatDarkModeEnabled() && theme !== 'light';
    if (!themeCustomized && !simpleView) return;
    try {
      if (window.sessionStorage.getItem(ACTIVE_EVENT_KEY)) return;
      window.sessionStorage.setItem(ACTIVE_EVENT_KEY, '1');
    } catch {
      return;
    }
    trackChatUxEvent('appearance_active', {
      entry: 'page_load',
      themeSetting: theme,
      themeEffective: effectiveTheme,
      simpleView,
    });
  }, [active, effectiveTheme, simpleView, theme]);
};
